// src/pages/ReferralLinkForm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    Container, Card, Form, Button, Alert, Spinner
} from 'react-bootstrap';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const ReferralLinkForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { t } = useTranslation();
    const isEditMode = !!id;

    const [formData, setFormData] = useState({
        name: '',
        poolHost: '',
        poolPort: 3333,
        poolWorker: '',
        poolPassword: 'x',
        active: true
    });

    const [loading, setLoading] = useState(false);
    const [initialLoading, setInitialLoading] = useState(isEditMode);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        if (isEditMode) {
            fetchReferralLink(id);
        }
    }, [id, isEditMode]);

    const fetchReferralLink = async (linkId) => {
        try {
            setInitialLoading(true);
            const response = await api.get(`/api/seller/referral-links/${linkId}`);
            if (response.data.success) {
                const link = response.data.data;
                setFormData({
                    name: link.name,
                    poolHost: link.poolHost,
                    poolPort: link.poolPort,
                    poolWorker: link.poolWorker,
                    poolPassword: link.poolPassword || 'x',
                    active: link.active
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || t('errors.loadingError'));
            console.error('Error fetching referral link:', err);
        } finally {
            setInitialLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        setSuccess('');

        // Валидация формы
        if (!formData.name || !formData.poolHost || !formData.poolWorker) {
            setError(t('auth.fillAllFields'));
            setLoading(false);
            return;
        }

        try {
            let response;

            if (isEditMode) {
                // Редактирование существующей ссылки
                response = await api.put(`/api/seller/referral-links/${id}`, formData);
                setSuccess(t('referral.linkUpdated'));
            } else {
                // Создание новой ссылки
                response = await api.post('/api/seller/referral-links', formData);
                setSuccess(t('referral.linkCreated'));
            }

            if (response.data.success) {
                // Редирект обратно к списку через 2 секунды
                setTimeout(() => {
                    navigate('/referrals/links');
                }, 2000);
            }
        } catch (err) {
            setError(err.response?.data?.message || t('errors.submissionError'));
            console.error('Error submitting referral link:', err);
        } finally {
            setLoading(false);
        }
    };

    if (initialLoading) {
        return (
            <Container className="my-5 text-center">
                <Spinner animation="border" variant="primary" />
                <p className="mt-3">{t('common.loading')}</p>
            </Container>
        );
    }

    return (
        <Container className="my-4">
            <h1 className="mb-4">
                {isEditMode ? t('referral.editLink') : t('referral.createLink')}
            </h1>

            <Card className="shadow-sm">
                <Card.Body>
                    {error && <Alert variant="danger">{error}</Alert>}
                    {success && <Alert variant="success">{success}</Alert>}

                    <Form onSubmit={handleSubmit}>
                        <Form.Group className="mb-3">
                            <Form.Label>{t('common.name')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="name"
                                value={formData.name}
                                onChange={handleChange}
                                placeholder={t('referral.enterName')}
                                required
                            />
                            <Form.Text className="text-muted">
                                {t('referral.nameInfo')}
                            </Form.Text>
                        </Form.Group>

                        <h5 className="mb-3">{t('flightSheet.poolSettings')}</h5>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('flightSheet.poolHost')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="poolHost"
                                value={formData.poolHost}
                                onChange={handleChange}
                                placeholder={t('flightSheet.enterPoolHost')}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('flightSheet.poolPort')}</Form.Label>
                            <Form.Control
                                type="number"
                                name="poolPort"
                                value={formData.poolPort}
                                onChange={handleChange}
                                placeholder={t('flightSheet.poolPort')}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('flightSheet.poolWorker')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="poolWorker"
                                value={formData.poolWorker}
                                onChange={handleChange}
                                placeholder={t('flightSheet.enterPoolWorker')}
                                required
                            />
                        </Form.Group>

                        <Form.Group className="mb-3">
                            <Form.Label>{t('flightSheet.poolPassword')}</Form.Label>
                            <Form.Control
                                type="text"
                                name="poolPassword"
                                value={formData.poolPassword}
                                onChange={handleChange}
                                placeholder={t('flightSheet.enterPoolPassword')}
                                required
                            />
                            <Form.Text className="text-muted">
                                {t('flightSheet.passwordInfo')}
                            </Form.Text>
                        </Form.Group>

                        {isEditMode && (
                            <Form.Group className="mb-3 form-check">
                                <Form.Check
                                    type="checkbox"
                                    id="active"
                                    name="active"
                                    label={t('common.active')}
                                    checked={formData.active}
                                    onChange={handleChange}
                                />
                                <Form.Text className="text-muted">
                                    {t('referral.activeInfo')}
                                </Form.Text>
                            </Form.Group>
                        )}

                        <div className="d-flex justify-content-between mt-4">
                            <Button
                                variant="outline-secondary"
                                onClick={() => navigate('/referrals/links')}
                                disabled={loading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button
                                variant="primary"
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Spinner
                                            as="span"
                                            animation="border"
                                            size="sm"
                                            role="status"
                                            aria-hidden="true"
                                            className="me-2"
                                        />
                                        {t('common.loading')}
                                    </>
                                ) : isEditMode ? t('common.save') : t('common.create')}
                            </Button>
                        </div>
                    </Form>
                </Card.Body>
            </Card>
        </Container>
    );
};

export default ReferralLinkForm;